<style>
  tr:hover td {
      background-color: #3F51B5;
  }
    .halfShareColor {color: #0f9e3e !important;}
    .refundedColor {color: #E91E63 !important;}
    .grossColor {color: #ffffff !important;}

</style>
<template>
  <div>
	<h1 class="page-header mb-3">Chat App Sales</h1>

  <!-- CARD 1 - summary strip -->
  <div class="row">
      <div class="col-xl-12">
        <div class="card border-0 mb-3 overflow-hidden bg-gray-800 text-white">
          <div class="card-body">
            <p>Day-by-day and month totals for Destiny Cards AI Chat token sales, for reconciling the 50% share Robert pays Steve.</p>

            <div class="row mb-3">
              <div class="col-md-3">
                <label class="form-label">From</label>
                <input type="date" class="form-control" v-model="from">
              </div>
              <div class="col-md-3">
                <label class="form-label">To</label>
                <input type="date" class="form-control" v-model="to">
              </div>
              <div class="col-md-6 d-flex align-items-end">
                <button class="btn btn-primary ms-2 fw-bold" @click="fetchData">Refresh <span class="fa fa-refresh"></span></button>
              </div>
            </div>

            <div v-if="truncated" class="alert alert-warning">
              <span class="fa fa-exclamation-triangle me-1"></span>
              The chat app truncated this result set &mdash; the totals below do NOT cover the whole range you asked for. Narrow the date range and run it again before reconciling.
            </div>

            <div v-if="error" class="alert alert-danger">
              {{ error }}
            </div>

            <div v-if="loading" class="loading text-center"><div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div></div>

            <div v-if="totals">
              <div class="row text-center">
                <div class="col-6 col-md-3 mb-3">
                  <div class="text-white-50 text-uppercase small">Gross</div>
                  <h3 class="mb-0 grossColor">{{ formatMoney(totals.gross) }}</h3>
                </div>
                <div class="col-6 col-md-3 mb-3">
                  <div class="text-white-50 text-uppercase small">Refunded</div>
                  <h3 class="mb-0 refundedColor">{{ formatMoney(totals.refunded) }}</h3>
                </div>
                <div class="col-6 col-md-3 mb-3">
                  <div class="text-white-50 text-uppercase small">Net</div>
                  <h3 class="mb-0">{{ formatMoney(totals.net) }}</h3>
                </div>
                <div class="col-6 col-md-3 mb-3">
                  <div class="text-white-50 text-uppercase small">Your 50% Share</div>
                  <h1 class="mb-0 text-success fw-bold">{{ formatMoney(totals.half) }}</h1>
                </div>
              </div>
              <div class="row text-center">
                <div class="col-6 col-md-3 mb-3">
                  <div class="text-white-50 text-uppercase small">Orders</div>
                  <h4 class="mb-0">{{ formatInt(totals.orders) }}</h4>
                </div>
                <div class="col-6 col-md-3 mb-3">
                  <div class="text-white-50 text-uppercase small">Tokens</div>
                  <h4 class="mb-0">{{ formatInt(totals.tokens) }}</h4>
                </div>
                <div class="col-6 col-md-3 mb-3">
                  <div class="text-white-50 text-uppercase small">Currency</div>
                  <h4 class="mb-0">{{ currency }}</h4>
                </div>
                <div class="col-6 col-md-3 mb-3">
                  <div class="text-white-50 text-uppercase small">Range</div>
                  <h4 class="mb-0">{{ from }} &rarr; {{ to }}</h4>
                </div>
              </div>
              <div class="text-white-50 small">
                50% share = net (gross minus refunds) x 0.5. Days bucketed in {{ timezone }}.
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>

  <!-- CARD 2 - monthly totals + daily net chart -->
  <div class="row">
      <div class="col-xl-12">
        <div class="card border-0 mb-3 overflow-hidden bg-gray-800 text-white">
          <div class="card-body">
            <h5 class="mb-3">Monthly Totals</h5>
            <vue-good-table
              :columns="monthColumns"
              v-if="monthRows && monthRows.length"
                :rows="monthRows"
              :lineNumbers="false"
              :search-options="{ enabled: true, skipDiacritics: true, placeholder: 'Search Current Table' }"
              :pagination-options="{ enabled: true,  position: 'bottom', perPage: 20, mode: 'pages' }"
              :selectOptions="{
                enabled: false,
                selectOnCheckboxOnly: true,
                selectionInfoClass: 'alert alert-info m-b-0 no-rounded-corner',
                selectionText: 'rows selected',
                clearSelectionText: 'clear',
              }"
              :sort-options="{
                enabled: true,
                multipleColumns: true,
              }"
            >
              <div slot="table-actions">
                <button class="btn btn-sm btn-outline-info" @click="fetchData">Refresh <span class="fa fa-refresh"></span></button>
              </div>
              <template slot="table-row" slot-scope="props">
                  <span v-if="props.column.field === 'half'" class="halfShareColor fw-bold">{{props.formattedRow[props.column.field]}}</span>
                  <span v-else>{{props.formattedRow[props.column.field]}}</span>
              </template>
            </vue-good-table>

            <div v-if="!loading && (!monthRows || !monthRows.length)" class="alert alert-info mb-0">
              No token sales in this range.
            </div>

            <div v-if="chartSeries[0] && chartSeries[0].data.length" class="mt-4">
              <h5 class="mb-3">Daily Net Revenue</h5>
              <apexchart type="bar" height="300" :options="chartOptions" :series="chartSeries"></apexchart>
            </div>

          </div>
        </div>
      </div>
    </div>

  <!-- CARD 3 - daily totals -->
  <div class="row">
      <div class="col-xl-12">
        <div class="card border-0 mb-3 overflow-hidden bg-gray-800 text-white">
          <div class="card-body">
            <h5 class="mb-3">Daily Totals</h5>
            <vue-good-table
              :columns="dayColumns"
              v-if="dayRows && dayRows.length"
                :rows="dayRows"
              :lineNumbers="false"
              :search-options="{ enabled: true, skipDiacritics: true, placeholder: 'Search Current Table' }"
              :pagination-options="{ enabled: true,  position: 'bottom', perPage: 20, mode: 'pages' }"
              :selectOptions="{
                enabled: false,
                selectOnCheckboxOnly: true,
                selectionInfoClass: 'alert alert-info m-b-0 no-rounded-corner',
                selectionText: 'rows selected',
                clearSelectionText: 'clear',
              }"
              :sort-options="{
                enabled: true,
                multipleColumns: true,
              }"
            >
              <template slot="table-row" slot-scope="props">
                  <span v-if="props.column.field === 'half'" class="halfShareColor fw-bold">{{props.formattedRow[props.column.field]}}</span>
                  <span v-else>{{props.formattedRow[props.column.field]}}</span>
              </template>
            </vue-good-table>

            <div v-if="!loading && (!dayRows || !dayRows.length)" class="alert alert-info mb-0">
              No token sales in this range.
            </div>

          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { useCookies } from "vue3-cookies";
import axios from "axios";
import moment from 'moment';
import ChatApi from '../config/ChatApi.vue';


export default {
  name: 'chatsales',
    setup() {
      const { cookies } = useCookies();
      return { cookies };
    },
    data() {
      return {
        loading: false,
        error: null,
        from: moment().startOf('month').format('YYYY-MM-DD'),
        to: moment().format('YYYY-MM-DD'),
        currency: 'USD',
        timezone: '',
        truncated: false,
        totals: null,
        monthColumns: [
          {
            label: 'Month',
            field: 'month',
            thClass: 'text-nowrap',
            tdClass: 'text-nowrap',
            sortable: true
          },
          {
            label: 'Orders',
            field: 'orders',
            thClass: 'text-nowrap',
            tdClass: 'text-nowrap',
            formatFn: this.formatInt,
            sortable: true
          },
          {
            label: 'Tokens',
            field: 'tokens',
            thClass: 'text-nowrap',
            tdClass: 'text-nowrap',
            formatFn: this.formatInt,
            sortable: true
          },
          {
            label: 'Gross',
            field: 'gross',
            thClass: 'text-nowrap',
            tdClass: 'text-nowrap',
            formatFn: this.formatMoney,
            sortable: true
          },
          {
            label: 'Refunded',
            field: 'refunded',
            thClass: 'text-nowrap',
            tdClass: 'text-nowrap',
            formatFn: this.formatMoney,
            sortable: true
          },
          {
            label: 'Net',
            field: 'net',
            thClass: 'text-nowrap',
            tdClass: 'text-nowrap',
            formatFn: this.formatMoney,
            sortable: true
          },
          {
            label: 'Your 50%',
            field: 'half',
            thClass: 'text-nowrap',
            tdClass: 'text-nowrap',
            formatFn: this.formatMoney,
            sortable: true
          }
        ],
        monthRows: null,
        dayColumns: [
          {
            label: 'Date',
            field: 'date',
            thClass: 'text-nowrap',
            tdClass: 'text-nowrap',
            sortable: true
          },
          {
            label: 'Orders',
            field: 'orders',
            thClass: 'text-nowrap',
            tdClass: 'text-nowrap',
            formatFn: this.formatInt,
            sortable: true
          },
          {
            label: 'Tokens',
            field: 'tokens',
            thClass: 'text-nowrap',
            tdClass: 'text-nowrap',
            formatFn: this.formatInt,
            sortable: true
          },
          {
            label: 'Gross',
            field: 'gross',
            thClass: 'text-nowrap',
            tdClass: 'text-nowrap',
            formatFn: this.formatMoney,
            sortable: true
          },
          {
            label: 'Refunded',
            field: 'refunded',
            thClass: 'text-nowrap',
            tdClass: 'text-nowrap',
            formatFn: this.formatMoney,
            sortable: true
          },
          {
            label: 'Net',
            field: 'net',
            thClass: 'text-nowrap',
            tdClass: 'text-nowrap',
            formatFn: this.formatMoney,
            sortable: true
          },
          {
            label: 'Your 50%',
            field: 'half',
            thClass: 'text-nowrap',
            tdClass: 'text-nowrap',
            formatFn: this.formatMoney,
            sortable: true
          }
        ],
        dayRows: null,
        chartSeries: [{ name: 'Net', data: [] }],
        chartOptions: {
          chart: {
            type: 'bar',
            height: 300,
            background: 'transparent',
            toolbar: { show: false }
          },
          theme: { mode: 'dark' },
          plotOptions: { bar: { columnWidth: '60%' } },
          dataLabels: { enabled: false },
          colors: ['#0f9e3e'],
          grid: { borderColor: '#4b5563' },
          xaxis: {
            categories: [],
            labels: { rotate: -45, style: { fontSize: '11px' } }
          },
          yaxis: { labels: { formatter: this.formatMoney } },
          tooltip: { y: { formatter: this.formatMoney } }
        }
      }
    },
    methods: {
      fetchData()
      {
        this.error = null;
        this.loading = true;

        axios
          .get(ChatApi.base + '/api/admin/token-sales', {
            headers: { 'x-admin-key': ChatApi.key },
            params: { from: this.from, to: this.to }
          })
          .then(response => {
            let data = response.data;
            this.loading = false;

            if (!data || !data.success) {
              this.error = 'The chat app did not return a successful result.';
              this.$swal.fire('Sales Report', 'The chat app did not return a successful result.', 'error');
              return;
            }

            this.currency = data.currency || 'USD';
            this.timezone = data.timezone || '';
            this.truncated = data.truncated === true;
            this.totals = data.totals || null;
            this.monthRows = data.months || [];
            this.dayRows = data.days || [];
            this.buildChart(this.dayRows);

            if (this.truncated) {
              this.$swal.fire('Truncated Result', 'The chat app truncated this result set. Narrow the date range before reconciling.', 'error');
            }
          })
          .catch(error => {
            this.loading = false;
            let serverError = error.response && error.response.data && error.response.data.message ? error.response.data.message : '';
            this.error = 'Error loading chat token sales.' + (serverError ? ' ' + serverError : '');
            this.$swal.fire('Sales Report', this.error, 'error');
            console.log(error);
          });
      },
      buildChart(days)
      {
        let categories = [];
        let values = [];
        let list = days || [];
        for (let i = 0; i < list.length; i++) {
          categories.push(list[i].date);
          values.push(Number(list[i].net));
        }
        this.chartOptions = Object.assign({}, this.chartOptions, {
          xaxis: Object.assign({}, this.chartOptions.xaxis, { categories: categories })
        });
        this.chartSeries = [{ name: 'Net', data: values }];
      },
      formatMoney(value)
      {
        let n = Number(value);
        if (value === null || value === undefined || value === '' || isNaN(n)) return '$0.00';
        return '$' + n.toFixed(2);
      },
      formatInt(value)
      {
        let n = Number(value);
        if (value === null || value === undefined || value === '' || isNaN(n)) return '0';
        return n.toLocaleString('en-US');
      }
    },
    mounted: function()
    {
      let username = this.cookies.get('username');
      let authenticated = this.cookies.get("authenticated");
      if (username === null || authenticated === null)
      {
        this.$router.push('/user/login-v3');
      }
      else {
        const crypto = require('crypto');
        const secret = 'GASEWkjshga@asdjfhasdfAA!fcaf@gdh#asdA';
        const hash = crypto.createHmac('sha256', secret)
            .update(authenticated)
            .digest('hex');
        if (username !== 'admin' || hash !== 'b8d32bd79ea384bb101f69e0935ee9c87be516c6642e39a479bf417b16354b2e') {
          this.$router.push('/user/login-v3');
        } else {
          this.fetchData();
        }
      }
    }
	}
</script>
